import React, { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { NAV, IMG } from "@/data/site";
import { useCart } from "@/lib/cart";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, setOpen } = useCart();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-ink/80 backdrop-blur-xl border-b border-white/10" : "bg-gradient-to-b from-black/70 to-transparent border-b border-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between h-[70px] md:h-[84px]">
          {/* Left: desktop nav */}
          <nav className="hidden lg:flex items-center gap-8 flex-1">
            {NAV.slice(0, 3).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-[0.18em] font-semibold transition-colors duration-300 hover:text-gold ${
                    isActive ? "text-gold" : "text-white/80"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Mobile hamburger */}
          <button
            data-testid="menu-toggle"
            aria-label="Menü öffnen"
            className="lg:hidden text-white p-2 -ml-2"
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Center: logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 lg:flex-none">
            <img src={IMG.logo} alt="STARSOX" className="h-11 w-11 md:h-12 md:w-12 object-contain" />
            <span className="font-heading text-xl md:text-2xl tracking-wide text-white leading-none">STARSOX</span>
          </Link>

          {/* Right: desktop rest nav + cart */}
          <div className="flex items-center gap-6 flex-1 justify-end">
            <nav className="hidden lg:flex items-center gap-8">
              {NAV.slice(3).map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `text-xs uppercase tracking-[0.18em] font-semibold transition-colors duration-300 hover:text-gold ${
                      isActive ? "text-gold" : "text-white/80"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <button
              data-testid="cart-button"
              aria-label="Warenkorb öffnen"
              onClick={() => setOpen(true)}
              className="relative text-white hover:text-gold transition-colors duration-300 p-1"
            >
              <ShoppingBag className="w-6 h-6" />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-1.5 -right-2 bg-gold text-ink text-[10px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-ink lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container-x flex items-center justify-between h-[70px]">
              <span className="font-heading text-xl">MENÜ</span>
              <button data-testid="menu-close" aria-label="Menü schließen" onClick={() => setMenuOpen(false)} className="p-2 -mr-2">
                <X className="w-7 h-7" />
              </button>
            </div>
            <nav className="container-x mt-8 flex flex-col">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={n.to}
                    data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                    className="block font-heading text-5xl uppercase py-3 text-white hover:text-gold transition-colors"
                  >
                    {n.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
